import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { useSettings } from '../../context/SettingsContext';
import {
  ArrowRightIcon,
  InstagramIcon,
  WhatsAppIcon,
  MailIcon,
  LocationOnIcon
} from '../Icons';
import { trackSocialClick } from '../../utils/analytics';

const Footer = ({ organization: orgProp }) => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  const { organization: contextOrg } = useSettings();
  const org = orgProp || contextOrg;

  return (
    <footer className="footer-modern">
      <div className="footer-wave">
        <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="shape-fill"></path>
        </svg>
      </div>

      <div className="footer-content container mx-auto px-6 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand/About Section */}
          <div className="footer-brand">
            <h2 className="text-headline-small font-bold text-white mb-4">
              {org.stammName || 'Pfadfinder Wutöschingen'}
            </h2>
            <p className="text-body-medium text-white/80 mb-6 leading-relaxed">
              {org.footerText}
            </p>
            <div className="footer-socials flex gap-4">
              {org.instagramUrl && (
                <a 
                  href={org.instagramUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-btn" 
                  aria-label="Instagram"
                  onClick={() => trackSocialClick('instagram')}
                >
                  <InstagramIcon />
                </a>
              )}
              {org.whatsappUrl && (
                <a 
                  href={org.whatsappUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-btn" 
                  aria-label="WhatsApp"
                  onClick={() => trackSocialClick('whatsapp')}
                >
                  <WhatsAppIcon />
                </a>
              )}
              {org.contactEmail && (
                <a 
                  href={`mailto:${org.contactEmail}`} 
                  className="social-icon-btn" 
                  aria-label="Email"
                  onClick={() => trackSocialClick('email')}
                >
                  <MailIcon />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-links-group">
            <h3 className="text-title-medium font-bold text-white mb-6 border-b-2 border-primary-light/30 pb-2 inline-block">
              Quick Links
            </h3>
            <ul className="footer-links-list space-y-3">
              <li><Link to="/" className="footer-link-modern">Home</Link></li>
              <li><Link to="/#blog-section" className="footer-link-modern">Blog & Neuigkeiten</Link></li>
              <li><Link to="/#events-section" className="footer-link-modern">Veranstaltungen</Link></li>
              <li><Link to="/contact" className="footer-link-modern">Kontakt</Link></li>
            </ul>
          </div>

          {/* Legal & Info */}
          <div className="footer-links-group">
            <h3 className="text-title-medium font-bold text-white mb-6 border-b-2 border-primary-light/30 pb-2 inline-block">
              Rechtliches
            </h3>
            <ul className="footer-links-list space-y-3">
              <li><Link to="/impressum" className="footer-link-modern">Impressum</Link></li>
              <li><Link to="/register" className="footer-link-modern">Mitglied werden</Link></li>
              <li><Link to="/login" className="footer-link-modern">Interner Bereich</Link></li>
            </ul>
          </div>

          {/* External / Bund */}
          <div className="footer-external">
            <h3 className="text-title-medium font-bold text-white mb-6 border-b-2 border-primary-light/30 pb-2 inline-block">
              Unser Bund
            </h3>
            <div className="bund-logo-card p-4 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors duration-300">
              <div className="bg-white/90 p-2 rounded-xl inline-block mb-3 shadow-sm">
                <img
                  src={org.bundLogoUrl || "https://wsrv.nl/?url=https%3A%2F%2Fpbhorizonte.de%2Fwp-content%2Fuploads%2F2021%2F02%2Fcropped-PBH-freigestellt.png&w=300&output=webp"}
                  alt={org.bundName || 'Pfadfinderbund Horizonte e.V.'}
                  width="120"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-auto object-contain"
                />
              </div>
              <p className="text-body-small text-white/85 mb-4">
                Wir sind stolzes Mitglied im {org.bundName || 'Pfadfinderbund Horizonte e.V.'}
              </p>
              {(org.bundUrl || 'https://pbhorizonte.de/') && (
                <a
                  href={org.bundUrl || 'https://pbhorizonte.de/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-label-large text-primary-light flex items-center gap-1 hover:gap-2 transition-all duration-300"
                >
                  Website besuchen <ArrowRightIcon sx={{ fontSize: 16 }} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-12 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

        {/* Bottom Bar */}
        <div className="footer-bottom flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-body-small text-white/80 text-center md:text-left">
            © {currentYear} {org.stammName || 'Pfadfinder Wutöschingen'}. Alle Rechte vorbehalten.
            <br className="md:hidden" />
            <span className="hidden md:inline"> | </span>
            {org.footerTagline}
          </p>
          <div className="footer-contact-badges flex flex-wrap justify-center gap-4">
            <div className="contact-badge">
              <LocationOnIcon sx={{ fontSize: 16 }} />
              <span>{org.locationBadge || 'Wutöschingen, DE'}</span>
            </div>
            <div className="contact-badge">
              <MailIcon sx={{ fontSize: 16 }} />
              <span>Gut Pfad!</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

